const S3_PUT_MAX_RETRIES = 3
const S3_PUT_RETRY_DELAY_MS = 1_000

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export interface S3UploadOptions {
  presignedUrl: string
  file: File
  contentType: string
  onProgress?: (percent: number) => void
}

// Plain XHR, not the app's axios instance — a presigned S3 URL must not carry the app's auth headers.
async function putToS3(options: S3UploadOptions): Promise<void> {
  const { presignedUrl, file, contentType, onProgress } = options
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', presignedUrl)
    xhr.setRequestHeader('Content-Type', contentType)

    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100))
        }
      })
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100)
        resolve()
      } else {
        reject(new Error(`S3 PUT failed: ${xhr.status} ${xhr.statusText}`))
      }
    })

    xhr.addEventListener('error', () => {
      reject(new Error('S3 PUT network error'))
    })

    xhr.send(file)
  })
}

export type UploadToS3Result =
  | { success: true }
  | { success: false; error: unknown }

export async function uploadToS3(
  options: S3UploadOptions
): Promise<UploadToS3Result> {
  let lastErr: unknown

  for (let attempt = 1; attempt <= S3_PUT_MAX_RETRIES; attempt += 1) {
    try {
      await putToS3(options)
      return { success: true } as const
    } catch (err) {
      lastErr = err
      if (attempt < S3_PUT_MAX_RETRIES) {
        await delay(S3_PUT_RETRY_DELAY_MS)
      }
    }
  }

  return { success: false, error: lastErr } as const
}
